import React, { useState, useRef, useEffect} from 'react'
import HighchartsReact from 'highcharts-react-official'
import Highcharts from 'highcharts'
import Cookie from "js-cookie";

const NavamGraph = ({hrData, rrData}) => {
    const[theme, setTheme] = useState();
    const[chartHeight, setChartHeight] = useState(300);
    const cookieData = Cookie.get("theme");
    
    const chartRef1 = useRef(null);
    const chartRef2 = useRef(null);
    const userInteractingRef = useRef(false);
    const frozenExtremesRef = useRef(null);

    // Define window parameters
    const WINDOW_MINUTES = 5; // 5 minute window for full-screen view
    const GAP_MINUTES = 1; // 1-minute buffer
    const windowSecs = WINDOW_MINUTES * 60;
    const gapSecs = GAP_MINUTES * 60;

    // Check if we have valid data
    const hasValidData = hrData && rrData && (hrData.length > 0 || rrData.length > 0);

    useEffect(() => {
        if (cookieData) {
            setTheme(cookieData)
        }
    },[cookieData]) 
    
    // Calculate dynamic height based on screen size
    const getChartHeight = () => {
        const screenHeight = window.innerHeight;
        const navbarHeight = 80; // Navbar height
        const patientDetailHeight = 100; // Reduced patient detail section height
        const availableHeight = screenHeight - navbarHeight - patientDetailHeight;
        return Math.max(Math.floor(availableHeight / 2.1), 200); // Reduced divisor for less gap, increased min height
    };

    useEffect(() => {
        const updateHeight = () => {
            setChartHeight(getChartHeight());
        };

        updateHeight(); // Set initial height
        window.addEventListener('resize', updateHeight);
        
        return () => window.removeEventListener('resize', updateHeight);
    }, []);

    // Calculate x-axis range based on data (working with milliseconds like Graph_new)
    const { xMin, xMax } = React.useMemo(() => {
        if (!hasValidData) {
            // If no data, show current time window
            const now = Date.now() / 1000;
            return {
                xMin: now - windowSecs,
                xMax: now,
            };
        }

        // Use HR data as primary, fallback to RR data
        const primaryData = hrData && hrData.length > 0 ? hrData : rrData || [];
        
        if (primaryData.length === 0) {
            // If no data, show current time window
            const now = Date.now() / 1000;
            return {
                xMin: now - windowSecs,
                xMax: now,
            };
        }

        // Data is in milliseconds, convert to seconds for calculation
        const firstPoint = primaryData[0]?.[0] / 1000;
        const lastPoint = primaryData[primaryData.length - 1][0] / 1000;
        const totalRange = lastPoint - firstPoint;

        let xMin, xMax;
        if (totalRange < windowSecs) {
            // If data is less than window size, center the data in the window
            const center = (firstPoint + lastPoint) / 2;
            xMin = center - windowSecs / 2;
            xMax = center + windowSecs / 2;
        } else {
            // If data is more than window size, show sliding window
            xMax = lastPoint + gapSecs;
            xMin = xMax - windowSecs;
        }

        return { xMin, xMax };
    }, [hrData, rrData, windowSecs, gapSecs, hasValidData]);

    // Sync extremes between charts
    const syncExtremes = (e, source) => {
        if (!e.trigger || e.trigger === 'sync') return;

        const isUserGesture = 
            e.trigger === 'pan' || 
            e.trigger === 'navigator' || 
            e.trigger === 'zoom' || 
            e.trigger === 'touchpan';

        if (isUserGesture) {
            userInteractingRef.current = true;
            frozenExtremesRef.current = {
                min: e.min,
                max: e.max,
            };

            // Resume auto-follow if user reaches live edge
            const primaryData = hrData && hrData.length > 0 ? hrData : rrData;
            if (primaryData && primaryData.length > 0) {
                const liveEdge = primaryData[primaryData.length - 1][0] + (gapSecs * 1000); // Convert to milliseconds
                if (Math.abs(liveEdge - e.max) < (gapSecs * 1000)) {
                    userInteractingRef.current = false;
                    frozenExtremesRef.current = null;
                }
            }
        }

        const { min, max } = e;

        // Sync from HR chart to RR chart
        if (source === 'hr' && chartRef2.current?.chart?.xAxis?.[0]) {
            chartRef2.current.chart.xAxis[0].setExtremes(min, max, true, false, {
                trigger: 'sync',
            });
        }

        // Sync from RR chart to HR chart
        if (source === 'rr' && chartRef1.current?.chart?.xAxis?.[0]) {
            chartRef1.current.chart.xAxis[0].setExtremes(min, max, true, false, {
                trigger: 'sync',
            });
        }
    };

    // Update chart extremes when data changes (like Graph_new)
    useEffect(() => {
        if (!hasValidData || !chartRef1.current?.chart || !chartRef2.current?.chart) return;

        // If user is interacting, keep frozen extremes
        if (userInteractingRef.current && frozenExtremesRef.current) {
            const { min, max } = frozenExtremesRef.current;
            chartRef1.current.chart.xAxis[0].setExtremes(min, max, false, false);
            chartRef2.current.chart.xAxis[0].setExtremes(min, max, false, false);
            return;
        }

        // Auto-follow mode: update to latest data (convert to milliseconds for Highcharts)
        if (xMin !== undefined && xMax !== undefined) {
            chartRef1.current.chart.xAxis[0].setExtremes(xMin * 1000, xMax * 1000, false, false);
            chartRef2.current.chart.xAxis[0].setExtremes(xMin * 1000, xMax * 1000, false, false);
        }
    }, [xMin, xMax, hasValidData]);

    // Check if new data goes beyond frozen window
    useEffect(() => {
        if (!hasValidData || !frozenExtremesRef.current) return;
        
        const primaryData = hrData && hrData.length > 0 ? hrData : rrData;
        if (!primaryData || primaryData.length === 0) return;

        const lastDataTs = primaryData[primaryData.length - 1][0]; // Already in milliseconds

        // If new data goes beyond frozen window, resume auto-follow
        if (lastDataTs > frozenExtremesRef.current.max) {
            userInteractingRef.current = false;
            frozenExtremesRef.current = null;
        }
    }, [hrData, rrData, hasValidData]);

    // Add resize observer for proper chart resizing
    useEffect(() => {
        if (!hasValidData) return;

        const chartContainer = document.querySelector('.navam-graph-container');
        
        if (!chartContainer) return;

        const resizeObserver = new ResizeObserver(() => {
            // Reflow both charts when container resizes
            if (chartRef1.current?.chart) {
                chartRef1.current.chart.reflow();
            }
            if (chartRef2.current?.chart) {
                chartRef2.current.chart.reflow();
            }
        });

        resizeObserver.observe(chartContainer);

        return () => {
            resizeObserver.disconnect();
        };
    }, [hasValidData]);

    // HR Chart Configuration (80-230 range) - Always define, even if no data
    const hrChartOptions = React.useMemo(() => ({
        chart: {
            backgroundColor: theme === "light-theme" ? "rgba(255, 255, 255, 1)" : '#0E0E0E',
            animation: false,
            height: chartHeight,
            width: null, // Let it take full container width
            panning: {
                enabled: true,
                type: 'x',
            },
            reflow: true, // Enable reflow
            style: {
                width: '100%',
                height: '100%',
            }
        },
        credits: {
            enabled: false
        },
        title: {
            text: 'Heart Rate (HR)',
            style: {
                color: theme === "light-theme" ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px',
                fontWeight: '600'
            }
        },
        tooltip: {
            formatter: function() {
                return '<b>HR: ' + this.y + ' BPM</b><br/>' +
                    new Date(this.x * 1000).toLocaleTimeString();
            }
        },
        xAxis: {
            type: 'datetime',
            labels: {
                enabled: true,
                y: 24,
                style: {
                    color: theme === "light-theme" ? 'rgba(127, 127, 127, 1)' : `rgba(255,255,255,0.7)`,
                    fontSize: '12px',
                    fontWeight: '500'
                },
                formatter: function () {
                    return new Date(this.value).toLocaleTimeString('en-US', {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit'
                    });
                },
            },
            min: undefined,
            max: undefined,
            startOnTick: false,
            endOnTick: false,
            lineWidth: 0,
            tickInterval: 60 * 1000, // 1 minute major ticks
            minorTickInterval: 15 * 1000, // 15 second minor ticks
            gridLineWidth: 1,
            gridLineColor: theme === "light-theme" ? 'rgba(127, 127, 127, 0.8)' : 'rgba(255, 255, 255, 0.8)',
            minorGridLineWidth: 0.3,
            minorGridLineColor: theme === "light-theme" ? 'rgba(127, 127, 127, 0.4)' : 'rgba(255, 255, 255, 0.4)',
            tickColor: theme === "light-theme" ? 'rgba(131, 130, 130, 1)' : `rgba(255,255,255,0.3)`,
            tickWidth: 0,
            events: {
                setExtremes: function (e) {
                    syncExtremes(e, 'hr');
                },
            },
        },
        yAxis: {
            title: {
                text: '',
                style: {
                    color: theme === "light-theme" ? 'rgba(127, 127, 127, 1)' : `rgba(255,255,255,0.7)`,
                },
            },
            labels: {
                enabled: true,
                x: -8,
                style: {
                    color: theme === "light-theme" ? 'rgba(127, 127, 127, 1)' : `rgba(255,255,255,0.7)`,
                    fontSize: '12px',
                    fontWeight: '500'
                },
            },
            plotBands: [], // Explicitly remove any plot bands
            lineWidth: 0.3,
            lineColor: theme === "light-theme" ? 'rgba(183, 183, 183, 1)' : `rgba(255,255,255,0.7)`,
            tickPositions: [0, 40, 80, 120, 160, 200], // Explicit tick positions
            min: 0,
            max: 200,
            startOnTick: false,
            endOnTick: false,
            gridLineColor: theme === "light-theme" ? 'rgba(127, 127, 127, 0.3)' : `rgba(255,255,255,0.3)`,
            tickColor: theme === "light-theme" ? 'rgba(127, 127, 127, 1)' : `rgba(255,255,255,0.3)`,
            minorGridLineColor: theme === "light-theme" ? 'rgba(177, 176, 176, 1)' : `rgba(255,255,255,0.3)`,
            minorGridLineWidth: 0.3,
        },
        legend: {
            enabled: false
        },
        plotOptions: {
            series: {
                type: 'line',
                turboThreshold: 0,
                gapSize: 5,
                gapUnit: 'value',
                linecap: 'round',
                marker: {
                    enabled: false,
                },
                states: {
                    hover: {
                        lineWidthPlus: 0,
                    },
                },
                animation: false,
                step: false,
            },
        },
        series: [
            {
                name: 'HR',
                data: hrData || [],
                connectNulls: true,
                color: '#22C55E', // Green color for HR
                lineWidth: 1.5,
                type: 'line',
                shadow: false,
                enableMouseTracking: true,
            }
        ]
    }), [theme, chartHeight, hrData]);

    // RR Chart Configuration (0-100 range) - Always define, even if no data
    const rrChartOptions = React.useMemo(() => ({
        chart: {
            backgroundColor: theme === "light-theme" ? "rgba(255, 255, 255, 1)" : '#0E0E0E',
            animation: false,
            height: chartHeight,
            width: null, // Let it take full container width
            panning: {
                enabled: true,
                type: 'x',
            },
            reflow: true, // Enable reflow
            style: {
                width: '100%',
                height: '100%',
            }
        },
        credits: {
            enabled: false
        },
        title: {
            text: 'Respiratory Rate (RR)',
            style: {
                color: theme === "light-theme" ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px',
                fontWeight: '600'
            }
        },
        tooltip: {
            formatter: function() {
                return '<b>RR: ' + this.y + ' BPM</b><br/>' +
                    new Date(this.x * 1000).toLocaleTimeString();
            }
        },
        xAxis: {
            type: 'datetime',
            labels: {
                enabled: true,
                y: 24,
                style: {
                    color: theme === "light-theme" ? 'rgba(127, 127, 127, 1)' : `rgba(255,255,255,0.7)`,
                    fontSize: '12px',
                    fontWeight: '500'
                },
                formatter: function () {
                    return new Date(this.value).toLocaleTimeString('en-US', { 
                        hour12: false, 
                        hour: '2-digit', 
                        minute: '2-digit' 
                    });
                },
            },
            min: undefined,
            max: undefined,
            startOnTick: false,
            endOnTick: false,
            lineColor: theme === "light-theme" ? 'rgba(183, 183, 183, 1)' : `rgba(255,255,255,0.7)`,
            lineWidth: 0.3,
            tickInterval: 60 * 1000, // 1 minute major ticks
            minorTickInterval: 15 * 1000, // 15 second minor ticks
            gridLineWidth: 1,
            gridLineColor: theme === "light-theme" ? 'rgba(127, 127, 127, 0.8)' : 'rgba(255, 255, 255, 0.8)',
            minorGridLineWidth: 0.3,
            minorGridLineColor: theme === "light-theme" ? 'rgba(127, 127, 127, 0.4)' : 'rgba(255, 255, 255, 0.4)',
            tickColor: theme === "light-theme" ? 'rgba(131, 130, 130, 1)' : `rgba(255,255,255,0.1)`,
            tickLength: 0,
            minorTickLength: 0,
            events: {
                setExtremes: function (e) {
                    syncExtremes(e, 'rr');
                },
            },
        },
        yAxis: {
            title: {
                text: '',
                style: {
                    color: theme === "light-theme" ? 'rgba(127, 127, 127, 1)' : `rgba(255,255,255,0.7)`,
                },
            },
            labels: {
                x: -8,
                style: {
                    color: theme === "light-theme" ? 'rgba(127, 127, 127, 1)' : `rgba(255,255,255,0.7)`,
                    fontSize: '12px',
                    fontWeight: '500'
                },
            },
            plotBands: [], // Explicitly remove any plot bands
            lineWidth: 0.3,
            lineColor: theme === "light-theme" ? 'rgba(183, 183, 183, 1)' : `rgba(255,255,255,0.7)`,
            tickPositions: [0, 20, 40, 60, 80, 100], // Explicit tick positions
            min: 0,
            max: 100,
            startOnTick: false,
            endOnTick: false,
            gridLineColor: theme === "light-theme" ? 'rgba(127, 127, 127, 0.3)' : `rgba(255,255,255,0.3)`,
            tickColor: theme === "light-theme" ? 'rgba(127, 127, 127, 1)' : `rgba(255,255,255,0.3)`,
            minorGridLineColor: theme === "light-theme" ? 'rgba(177, 176, 176, 1)' : `rgba(255,255,255,0.3)`,
            minorGridLineWidth: 0.3,
        },
        legend: {
            enabled: false
        },
        plotOptions: {
            series: {
                type: 'line',
                turboThreshold: 0,
                gapSize: 5,
                gapUnit: 'value',
                linecap: 'round',
                marker: {
                    enabled: false,
                },
                states: {
                    hover: {
                        lineWidthPlus: 0,
                    },
                },
                animation: false,
                step: false,
            },
        },
        series: [
            {
                name: 'RR',
                data: rrData || [],
                connectNulls: true,
                color: '#FACC15', // Yellow color for RR (like in the image)
                lineWidth: 1.5,
                type: 'line',
                shadow: false,
                enableMouseTracking: true,
            }
        ]
    }), [theme, chartHeight, rrData]);

    // Early return after all hooks are called
    if (!hasValidData) {
        return (
            <div className="navam-graph-container" style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '400px',
                color: theme === "light-theme" ? '#666' : '#ccc'
            }}>
                <p>No graph data available for the SpO₂ device.</p>
            </div>
        );
    }

  return (
    <>
    { 
        <div className="navam-graph-container">
            <div className="navam-chart-wrapper">
                <HighchartsReact
                    ref={chartRef1}
                    highcharts={Highcharts}
                    options={hrChartOptions}
                    constructorType={'chart'}
                    allowChartUpdate={true}
                    className={`navam-chart ${theme === "light-theme" ? "highcharts-light" : "highcharts-dark"}`} 
                />
            </div>

            <div className="navam-chart-wrapper">
                <HighchartsReact
                    ref={chartRef2}
                    highcharts={Highcharts}
                    options={rrChartOptions}
                    constructorType={'chart'}
                    allowChartUpdate={true}
                    className={`navam-chart ${theme === "light-theme" ? "highcharts-light" : "highcharts-dark"}`} 
                />
            </div>
        </div> 
    }
    </>
  )
}

export default NavamGraph

